// ./src/utils/api/EventGameService.ts
//
// Live Party Games (#34) — a host configures a secret-role game on an event, adds
// roles (markers), and deals live rounds over the guest list. Mirrors the
// eventsmagic-api EventGame / EventGameMarker SDL. `runRound` (eventGameRunRound)
// CSPRNG-deals a fresh round of one marker per player; `setOutcome`
// (eventGameSetOutcome) eliminates / marks a player and reveals their marker.
import { gql } from '@apollo/client';
import { OpResult } from '@sdflc/api-helpers';

import { ApiService } from './ApiService';
import { Pagination } from './commonApiTypes';

const MARKER_FIELDS = `
  id
  accountId
  eventId
  gameId
  name
  color
  icon
  team
  visibility
  composition
  payload
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const ASSIGNMENT_FIELDS = `
  id
  gameRoundId
  gameId
  eventGuestId
  personId
  markerId
  displayName
  markerLabel
  markerColor
  payload
  checklist
  outcome
  revealed
  revealedAt
  targetEventGuestId
  orderNo
  createdAt
`;

const ROUND_FIELDS = `
  id
  gameId
  eventId
  roundNo
  roundState
  audienceSize
  drawAlgorithm
  drawCount
  startedAt
  endedAt
  lastDrawnByUserId
  lastDrawnByEventGuestId
  lastDrawnByLabel
  status
  assignments { ${ASSIGNMENT_FIELDS} }
  createdAt
`;

// The list needs the game + its roles (role count, config), but NOT every round's
// assignments (loaded lazily in the play drawer via get()).
const GAME_LIST_FIELDS = `
  id
  eventId
  name
  description
  preset
  assignmentKind
  revealRule
  winRules
  checklistTemplate
  notes
  orderNo
  status
  rights
  markers { ${MARKER_FIELDS} }
  createdAt
  updatedAt
`;

const GAME_FULL_FIELDS = `
  ${GAME_LIST_FIELDS}
  rounds { ${ROUND_FIELDS} }
`;

const LIST_QUERY = gql`
  query EventGameList($filter: EventGameFilter, $params: PaginationAndSorting) {
    eventGameList(filter: $filter, params: $params) {
      code
      total
      data { ${GAME_LIST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const GET_QUERY = gql`
  query EventGameGet($id: ID) {
    eventGameGet(id: $id) {
      code
      data { ${GAME_FULL_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation EventGameCreate($params: EventGameInput) {
    eventGameCreate(params: $params) {
      code
      data { ${GAME_LIST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation EventGameUpdate($where: EventGameWhereInput, $params: EventGameInput) {
    eventGameUpdate(where: $where, params: $params) {
      code
      data { ${GAME_LIST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation EventGameRemove($where: EventGameWhereInput) {
    eventGameRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

const RUN_ROUND_MUTATION = gql`
  mutation EventGameRunRound($params: EventGameRunRoundInput) {
    eventGameRunRound(params: $params) {
      code
      data { ${ROUND_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const SET_OUTCOME_MUTATION = gql`
  mutation EventGameSetOutcome($params: EventGameSetOutcomeInput) {
    eventGameSetOutcome(params: $params) {
      code
      data { ${ASSIGNMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const STREAM_TICKET_MUTATION = gql`
  mutation EventGameStreamTicket($eventId: ID!) {
    eventGameStreamTicket(eventId: $eventId) {
      code
      data { ticket }
      errors { name errors warnings }
    }
  }
`;

const MARKER_LIST_QUERY = gql`
  query EventGameMarkerList($filter: EventGameMarkerFilter, $params: PaginationAndSorting) {
    eventGameMarkerList(filter: $filter, params: $params) {
      code
      total
      data { ${MARKER_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const MARKER_CREATE_MUTATION = gql`
  mutation EventGameMarkerCreate($params: EventGameMarkerInput) {
    eventGameMarkerCreate(params: $params) {
      code
      data { ${MARKER_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const MARKER_UPDATE_MUTATION = gql`
  mutation EventGameMarkerUpdate($where: EventGameMarkerWhereInput, $params: EventGameMarkerInput) {
    eventGameMarkerUpdate(where: $where, params: $params) {
      code
      data { ${MARKER_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const MARKER_REMOVE_MUTATION = gql`
  mutation EventGameMarkerRemove($where: EventGameMarkerWhereInput) {
    eventGameMarkerRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

export type EventGameAssignmentKind = "role" | "target" | "pair";
export type EventGameMarkerVisibility = "self" | "others" | "all";
export type EventGameCompositionType = "fixed" | "ratio" | "rest" | "bracket";
export type EventGameRevealRule = "on_elimination" | "at_end" | "never";
export type EventGameAssignmentOutcome = "in_play" | "eliminated" | "won" | "lost";

export interface EventGameComposition {
  type?: EventGameCompositionType;
  value?: number | null;
  brackets?: { min: number; max: number; count: number }[] | null;
}

export interface EventGameMarker {
  id?: string;
  accountId?: string | null;
  eventId?: string | null;
  gameId?: string | null;
  name?: string | null;
  color?: string | null;
  icon?: string | null;
  team?: string | null;
  visibility?: EventGameMarkerVisibility | null;
  composition?: EventGameComposition | null;
  payload?: Record<string, unknown> | null;
  orderNo?: number | null;
  status?: number | null;
  rights?: string[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface EventGameAssignment {
  id?: string;
  gameRoundId?: string | null;
  gameId?: string | null;
  eventGuestId?: string | null;
  personId?: string | null;
  markerId?: string | null;
  displayName?: string | null;
  markerLabel?: string | null;
  markerColor?: string | null;
  payload?: Record<string, unknown> | null;
  checklist?: Record<string, unknown> | null;
  outcome?: EventGameAssignmentOutcome | null;
  revealed?: boolean | null;
  revealedAt?: string | null;
  targetEventGuestId?: string | null;
  orderNo?: number | null;
  createdAt?: string | null;
}

export interface EventGameRound {
  id?: string;
  gameId?: string | null;
  eventId?: string | null;
  roundNo?: number | null;
  roundState?: string | null;
  audienceSize?: number | null;
  drawAlgorithm?: string | null;
  drawCount?: number | null;
  startedAt?: string | null;
  endedAt?: string | null;
  lastDrawnByUserId?: string | null;
  lastDrawnByEventGuestId?: string | null;
  lastDrawnByLabel?: string | null;
  status?: number | null;
  assignments?: EventGameAssignment[] | null;
  createdAt?: string | null;
}

export interface EventGame {
  id?: string;
  eventId?: string | null;
  name?: string | null;
  description?: string | null;
  preset?: string | null;
  assignmentKind?: EventGameAssignmentKind | null;
  revealRule?: EventGameRevealRule | null;
  winRules?: Record<string, unknown> | null;
  checklistTemplate?: Record<string, unknown> | null;
  notes?: string | null;
  orderNo?: number | null;
  status?: number | null;
  rights?: string[] | null;
  markers?: EventGameMarker[] | null;
  rounds?: EventGameRound[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface EventGameInput {
  eventId?: string | null;
  name?: string | null;
  description?: string | null;
  preset?: string | null;
  assignmentKind?: EventGameAssignmentKind | null;
  revealRule?: EventGameRevealRule | null;
  winRules?: Record<string, unknown> | null;
  checklistTemplate?: Record<string, unknown> | null;
  notes?: string | null;
  orderNo?: number | null;
  status?: number | null;
}

export interface EventGameMarkerInput {
  gameId?: string | null;
  name?: string | null;
  color?: string | null;
  icon?: string | null;
  team?: string | null;
  visibility?: EventGameMarkerVisibility | null;
  composition?: EventGameComposition | null;
  payload?: Record<string, unknown> | null;
  orderNo?: number | null;
  status?: number | null;
}

export interface EventGameRunRoundInput {
  id: string;
  /** The audience to deal to (guest ids); omit to deal to all active attendees. */
  eventGuestIds?: string[] | null;
  audienceFilter?: Record<string, unknown> | null;
}

export interface EventGameSetOutcomeInput {
  id: string;
  outcome: EventGameAssignmentOutcome;
  revealed?: boolean | null;
}

export class EventGameService {
  /** One page of an event's games, by display order, with `total` (ROADMAP #112 — it read every game at once). */
  static async list(eventId: string, statuses: number[] | undefined, pagination: Pagination): Promise<OpResult> {
    return ApiService.executeQuery(
      LIST_QUERY,
      { filter: { eventId: [eventId], status: statuses }, params: { pagination } },
      { fetchPolicy: "network-only" }
    );
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(GET_QUERY, { id }, { fetchPolicy: "network-only" });
  }

  static async create(params: EventGameInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  static async update(id: string | string[], params: EventGameInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string | string[]): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }

  /** Deal a fresh round of the game at random from the audience. */
  static async runRound(params: EventGameRunRoundInput): Promise<OpResult> {
    return ApiService.executeMutation(RUN_ROUND_MUTATION, { params });
  }

  /** Mark one player's outcome (eliminate / won / lost) + reveal per the game's rule. */
  static async setOutcome(params: EventGameSetOutcomeInput): Promise<OpResult> {
    return ApiService.executeMutation(SET_OUTCOME_MUTATION, { params });
  }

  /** Mint a short-lived ticket to open the live game SSE stream. */
  static async streamTicket(eventId: string): Promise<OpResult> {
    return ApiService.executeMutation(STREAM_TICKET_MUTATION, { eventId });
  }
}

export class EventGameMarkerService {
  static async list(gameId: string, statuses?: number[]): Promise<OpResult> {
    return ApiService.executeQuery(MARKER_LIST_QUERY, { filter: { gameId: [gameId], status: statuses } }, { fetchPolicy: "network-only" });
  }

  static async create(params: EventGameMarkerInput): Promise<OpResult> {
    return ApiService.executeMutation(MARKER_CREATE_MUTATION, { params });
  }

  static async update(id: string | string[], params: EventGameMarkerInput): Promise<OpResult> {
    return ApiService.executeMutation(MARKER_UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string | string[]): Promise<OpResult> {
    return ApiService.executeMutation(MARKER_REMOVE_MUTATION, { where: { id } });
  }
}
